import { useConnectWalletButtonStates } from '@shared/hooks/use-connect-wallet-button-states';
import { BaseOffer, GuildOffer } from '@shared/types/offers';
import React, { useMemo } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { Loader } from '../loader';

interface PurchaseButtonProps {
  isWalletConnected: boolean
  transactionPending: boolean
  onPurchase: VoidFunction
  isInsufficientBalance?: boolean
  isWalletMatched: boolean
  isLimitReached?: boolean
  isSoldOut?: boolean
  offer: BaseOffer
  selectedQuantity: number
  selectedMembers: string[]
  isAccountLoading: boolean
  currency?: 'POWER' | 'FIAT'
}

export const PurchaseButton: React.FC<PurchaseButtonProps> = ({
  isWalletConnected,
  transactionPending,
  onPurchase,
  isInsufficientBalance,
  isWalletMatched,
  isSoldOut,
  isLimitReached,
  offer,
  selectedQuantity,
  selectedMembers,
  isAccountLoading,
  currency = 'POWER',
}) => {
  const { offerCardTranslations, offerCheckoutTranslations } = useTranslation();
  const { buttonText, buttonAction } = useConnectWalletButtonStates();

  const exceedsLimit = useMemo(() => {
    if (selectedMembers.length === 0 || !('guildMemberSupplies' in offer)) {
      return false;
    }

    return selectedMembers.some((memberId) => {
      const remainingSupply = (offer as GuildOffer).guildMemberSupplies[memberId]
        ?? (offer as GuildOffer).maxMemberSupply;
      return (selectedQuantity || 1) > remainingSupply;
    });
  }, [selectedMembers, selectedQuantity, offer]);

  const noMembersSelected = useMemo(() => {
    if (!('guildMemberSupplies' in offer)) {
      return false;
    }
    return selectedMembers.length === 0;
  }, [selectedMembers, offer]);

  const isFiat = useMemo(() => currency === 'FIAT', [currency]);
  const requiresWallet = !isFiat;

  if (isFiat || (isWalletMatched && isWalletConnected)) {
    if (isLimitReached || isSoldOut) {
      return (
        <button
          type="button"
          className="green-button-button font-border poppins-2xl h-[66px] w-full hover:brightness-125"
          disabled
        >
          {isLimitReached
            ? offerCardTranslations.limitReached
            : offerCardTranslations.soldOut}
        </button>
      );
    }

    if (requiresWallet && isInsufficientBalance && !isAccountLoading) {
      return (
        <button
          type="button"
          className="red-button font-border poppins-2xl h-[66px] w-full"
        >
          {offerCheckoutTranslations.insufficientBalance}
        </button>
      );
    }

    return (
      <button
        type="button"
        disabled={
          (requiresWallet && !isWalletConnected)
          || ('guildMemberSupplies' in offer && exceedsLimit)
          || noMembersSelected
          || (requiresWallet && isAccountLoading)
        }
        onClick={() => !transactionPending && onPurchase()}
        className="green-button font-border poppins-2xl h-[66px] w-full hover:brightness-125"
      >
        {transactionPending || (requiresWallet && isAccountLoading) ? (
          <Loader
            width={48}
            height={48}
          />
        ) : (
          offerCheckoutTranslations.confirmPurchase
        )}
      </button>
    );
  }

  if (requiresWallet) {
    return (
      <button
        type="button"
        className="green-button font-border poppins-2xl h-[66px] w-full uppercase hover:brightness-125"
        onClick={buttonAction}
      >
        {buttonText}
      </button>
    );
  }

  return (
    <button
      type="button"
      disabled={
        ('guildMemberSupplies' in offer && exceedsLimit)
        || noMembersSelected
      }
      onClick={() => !transactionPending && onPurchase()}
      className="green-button font-border poppins-2xl h-[66px] w-full hover:brightness-125"
    >
      {transactionPending ? (
        <Loader
          width={48}
          height={48}
        />
      ) : (
        offerCheckoutTranslations.confirmPurchase
      )}
    </button>
  );
};
